import React from 'react';
import type { CustomerResponse } from '@troia-v3/types';
import { Avatar, AvatarImage, AvatarFallback, StatusPill } from '@/shared/components/ui';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { Icon } from '@/shared/icons';
import { getCustomerTypeBadge } from './customerTypeBadge';

/** Iniciais do nome do cliente (2 letras). Avatar sem foto usa slate (AvatarFallback). */
function getCustomerInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0] ?? '').slice(0, 2).toUpperCase();
  return `${parts[0]?.[0] ?? ''}${parts[parts.length - 1]?.[0] ?? ''}`.toUpperCase();
}

/**
 * Linha de tabela de cliente (view "Lista"). Padrão-lista travado (DS 3.x,
 * espelha `UserListItem`): checkbox de seleção (1ª col) + célula multi-linha
 * (nome + documento) + menu ••• (última col). Avatar sem foto = slate.
 */
export interface CustomerListItemProps {
  customer: CustomerResponse;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
  onOpen?: (id: string) => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
}

export const CustomerListItem: React.FC<CustomerListItemProps> = ({
  customer,
  selected = false,
  onToggleSelect,
  onOpen,
  onEdit,
  onDelete,
}) => {
  const typeBadge = getCustomerTypeBadge(customer.type);
  const isActive = customer.status === 'active';
  const contact = customer.email || customer.phone || '—';

  const actions: CardAction[] = [];
  if (onOpen) actions.push({ label: 'Abrir cliente', icon: 'user', onClick: () => onOpen(customer.id) });
  if (onEdit) actions.push({ label: 'Editar', icon: 'edit', onClick: () => onEdit(customer.id) });
  if (onDelete) actions.push({ label: 'Excluir', icon: 'trash', onClick: () => onDelete(customer.id), variant: 'danger' });

  return (
    <tr
      className="border-b transition-colors cursor-pointer"
      style={{
        borderColor: 'var(--theme-card-border)',
        backgroundColor: selected ? 'var(--theme-primary-background)' : undefined,
        opacity: isActive ? 1 : 0.68,
      }}
      onMouseEnter={(e) => {
        if (!selected) e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
      }}
      onMouseLeave={(e) => {
        if (!selected) e.currentTarget.style.backgroundColor = 'transparent';
      }}
      onClick={() => onOpen?.(customer.id)}
      data-testid={`customers-row-${customer.id}`}
    >
      {/* Checkbox */}
      <td className="pl-4 pr-1 py-3 w-11" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={() => onToggleSelect?.(customer.id)}
          className="w-[18px] h-[18px] rounded-[5px] border-[1.6px] flex items-center justify-center transition-colors"
          style={{
            backgroundColor: selected ? 'var(--theme-primary)' : 'transparent',
            borderColor: selected ? 'var(--theme-primary)' : 'var(--theme-input-border)',
            color: '#ffffff',
          }}
          aria-label={selected ? 'Desmarcar' : 'Selecionar'}
          data-testid={`customers-check-${customer.id}`}
        >
          {selected && <Icon name="check" className="w-3 h-3" />}
        </button>
      </td>

      {/* Cliente: avatar + nome (+ documento) */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-3 min-w-0">
          {/* F44: avatar de pessoa 1ª col = squircle 36 */}
          <Avatar className="w-9 h-9 flex-shrink-0" isSquircle>
            {customer.picture && <AvatarImage src={customer.picture} alt={customer.name} isSquircle />}
            <AvatarFallback isSquircle>
              <span className="text-white font-semibold text-xs">{getCustomerInitials(customer.name)}</span>
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="font-semibold text-sm truncate" style={{ color: 'var(--theme-card-text)' }}>
              {customer.name}
            </div>
            {customer.document && (
              <div className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
                {customer.document}
              </div>
            )}
          </div>
        </div>
      </td>

      {/* Tipo */}
      <td className="px-4 py-3">
        <span
          className="inline-flex items-center gap-1.5 h-[23px] px-2.5 rounded-full text-xs font-semibold"
          style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-card-text)' }}
        >
          <Icon name={typeBadge.icon} className="w-3 h-3" />
          {typeBadge.label}
        </span>
      </td>

      {/* Segmento */}
      <td className="px-4 py-3 text-sm" style={{ color: 'var(--theme-muted-text)' }}>
        {customer.segment || '—'}
      </td>

      {/* Contato */}
      <td className="px-4 py-3 text-sm truncate" style={{ color: 'var(--theme-muted-text)' }}>
        {contact}
      </td>

      {/* Status (F62: StatusPill shared) */}
      <td className="px-4 py-3">
        <StatusPill
          variant={isActive ? 'success' : 'neutral'}
          label={isActive ? 'Ativo' : 'Inativo'}
          data-testid={`customers-row-status-${customer.id}`}
        />
      </td>

      {/* Ações */}
      <td className="px-4 py-3 text-right w-14" onClick={(e) => e.stopPropagation()}>
        {actions.length > 0 && (
          <CardActionsMenu actions={actions} size="sm" data-testid={`customers-row-actions-${customer.id}`} />
        )}
      </td>
    </tr>
  );
};

CustomerListItem.displayName = 'CustomerListItem';
